/**
 * The E-Mail-Absender page's data (T1b): the settings with their form, and the
 * change history. Two hooks, one per endpoint, so a slow history never holds the
 * form back and a failed one never hides it.
 *
 * ONE READ EACH ON MOUNT, NONE AFTERWARDS ON THEIR OWN. No polling. The settings
 * are read once; after a save the PATCH answer IS the new state, so they are not
 * read again. The history is read again once after a save that changed
 * something, because that save just wrote the entry it should now show
 * (Regel 49) — and not after one that changed nothing, which wrote none.
 *
 * AN ANSWER THAT IS NO LONGER WANTED IS DROPPED. Each read carries a sequence
 * number and only the newest may write state, so a retry, a StrictMode remount
 * or a reload after a save can never be overwritten by an older answer.
 *
 * `active` is the page's role gate, as a boolean: a non-admin triggers no read
 * at all. It must stay a boolean — the auth context is rebuilt on every render
 * of its provider, and an effect depending on that object would read in a loop.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchAdminEmailSettings,
  fetchAdminEmailSettingsActivity,
  updateAdminEmailSettings,
} from "../../../api/adminData";
import {
  ACTIVITY_ERROR,
  LOAD_ERROR,
  buildPatch,
  fieldErrorsFromError,
  isDirty,
  sameStoredValues,
  saveErrorMessage,
  settingsToForm,
  validateForm,
} from "./emailSettingsModel";

export function useEmailSettings({ active }) {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(() => settingsToForm(null));
  const [initial, setInitial] = useState(() => settingsToForm(null));
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const seq = useRef(0);
  // The click guard in its own right: `saving` is state and lags a render, so
  // a second submit in the same tick would still see `false`. It is released
  // only once the save's answer is COMMITTED (the effect below), not when the
  // request settles: until React has rendered that answer, the form still holds
  // the previous submit handler, whose `save` sees the old draft and baseline
  // and would send the same PATCH a second time.
  const inFlight = useRef(false);

  const load = useCallback(() => {
    if (!active) return;
    const mine = seq.current + 1;
    seq.current = mine;
    setLoading(true);
    setLoadError("");
    fetchAdminEmailSettings()
      .then((res) => {
        if (seq.current !== mine) return;
        const next = settingsToForm(res);
        setSettings(res);
        setForm(next);
        setInitial(next);
      })
      .catch(() => {
        if (seq.current !== mine) return;
        setLoadError(LOAD_ERROR);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  // Bumped once per settled save. An effect on it runs after the commit that
  // shows the answer, whatever else that render batched — `saving` alone could
  // read `false` before and after and never trigger it.
  const [settled, setSettled] = useState(0);
  useEffect(() => {
    inFlight.current = false;
  }, [settled]);

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  /**
   * Send the changed fields. Resolves to what happened, for the page to report:
   * `{ status: "saved", changed }`, `{ status: "invalid" }` (refused here, nothing
   * sent), `{ status: "failed", message }`, or `{ status: "idle" }` when there was
   * nothing to send or a save is already running.
   *
   * A failure keeps the form exactly as typed and `initial` where it was, so the
   * change is still pending — an error must never cost the person their edits,
   * and nothing is shown as saved that the server did not store.
   */
  const save = useCallback(async () => {
    if (inFlight.current || !settings) return { status: "idle" };
    const patch = buildPatch(form, initial);
    if (!Object.keys(patch).length) return { status: "idle" };

    const clientErrors = validateForm(form, initial);
    if (Object.keys(clientErrors).length) {
      setFieldErrors(clientErrors);
      setSaveError(null);
      return { status: "invalid", fields: Object.keys(clientErrors) };
    }

    inFlight.current = true;
    setSaving(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const res = await updateAdminEmailSettings(patch);
      const next = settingsToForm(res);
      const changed = !sameStoredValues(res, settings);
      setSettings(res);
      setForm(next);
      setInitial(next);
      return { status: "saved", changed };
    } catch (err) {
      const fields = fieldErrorsFromError(err);
      const message = saveErrorMessage(err);
      setFieldErrors(fields);
      setSaveError({ message, status: err?.status ?? null });
      return { status: "failed", message, fields: Object.keys(fields) };
    } finally {
      // `inFlight` stays closed until this render is committed — see above.
      setSaving(false);
      setSettled((n) => n + 1);
    }
  }, [settings, form, initial]);

  return {
    settings,
    form,
    loading: loading || (active && !settings && !loadError),
    loadError,
    saving,
    saveError,
    fieldErrors,
    dirty: isDirty(form, initial),
    reload: load,
    setField,
    save,
  };
}

export function useEmailSettingsActivity({ active }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const seq = useRef(0);

  const load = useCallback(() => {
    if (!active) return;
    const mine = seq.current + 1;
    seq.current = mine;
    setLoading(true);
    setError("");
    fetchAdminEmailSettingsActivity()
      .then((res) => {
        if (seq.current !== mine) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total) || 0);
        setLoaded(true);
      })
      .catch(() => {
        if (seq.current !== mine) return;
        setError(ACTIVITY_ERROR);
      })
      .finally(() => {
        if (seq.current === mine) setLoading(false);
      });
  }, [active]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    items,
    total,
    // The first answer is awaited as loading; a reload after a save keeps the
    // list on screen instead of blanking it for the time of one request.
    loading: !loaded && !error && (loading || active),
    refreshing: loaded && loading,
    error,
    reload: load,
  };
}
